import { assetsTypes, ProductData } from "ecom-types";
import { IUsePdpFilterOptions } from "~/composables/usePdpFilter";

export interface IPdpImagesData {
  url: string;
  alt?: string;
  type: string;
  listType?: string;
}

export interface IUsePdpOptions {
  pdpFilterOptions?: IUsePdpFilterOptions;
}

export const usePdp = (sizesSlugs: string[] = [], options?: IUsePdpOptions) => {
  const generalStore = useGeneralStore();
  const _options = options || {};
  const oldConfigurationSKU = ref("");
  const configurationError = ref(false);

  const route = useRoute();
  const router = useRouter();

  const productResult = useProduct(null, sizesSlugs);

  const pdpFilters = usePdpFilter(
      productResult.productInfo as Ref<ProductData>,
      productResult.configurationInfo,
      sizesSlugs,
      _options.pdpFilterOptions
  );

  const SizeMaterialCategory = computed(() => {
    return pdpFilters.filteredMaterialsByCategories.value?.find((el) =>
      sizesSlugs.includes(el.slug),
    );
  });

  const currentSku = computed({
    get() {
      return (
        (route.query.configuration_id as string) ||
        productResult.productInfo.value?.default_sku ||
        ""
      );
    },
    async set(val: string) {
      await router.replace({
        path: route.path,
        query: {
          ...route.query,
          configuration_id: val,
        },
      });
    },
  });

  const images = computed<IPdpImagesData[]>(() => {
    const imageArr: IPdpImagesData[] = [];
    const mainImages = productResult.configurationInfo.value?.medias?.reduce((acc, el) => {
      if (el?.position?.includes("PDP")) {
        acc.push({
          url: el?.file?.url,
          alt: el?.alt || productResult.productTitle.value || "Image",
          type: el?.file?.media_type?.includes("video")
            ? assetsTypes.video
            : assetsTypes.image,
          listType: "render",
        });
      }
      return acc;
    }, [] as IPdpImagesData[]);

    if (mainImages?.length) {
      imageArr.push(...mainImages);
    }

    productResult.productInfo.value?.marketing_images.forEach((item) => {
      imageArr.push({
        type: item?.media_type?.includes("video")
          ? assetsTypes.video
          : assetsTypes.image,
        url: item?.url,
        alt: item.alt || productResult.productTitle.value || "Image",
        listType: "media",
      });
    });
    return imageArr as IPdpImagesData[];
  });

  const isReadyToPay = computed(() => {
    return !!productResult.productInfo.value && !!productResult.configurationInfo.value && !!generalStore.cart?.unique_id;
  })

  const isForceToPay = computed(() => {
    return route.query?.["force-to-pay"] === "1" && isReadyToPay.value;
  })


  watch(
    productResult.configurationInfo,
    (val) => {
      if (val && !pdpFilters.clickOnAnyFilter.value) {
        pdpFilters.setResultData();
      } else if (pdpFilters.clickOnAnyFilter.value && val) {
        pdpFilters.clickOnAnyFilter.value = false;
      }

      if (val) {
        oldConfigurationSKU.value = currentSku.value;
      }
    },
    {
      immediate: true,
    },
  );

  watchEffect(() => {
    if (productResult.configurationInfo.value && configurationError.value) {
      configurationError.value = false;
      pdpFilters.setResultData();
    }
  });

  return {
    currentSku,
    oldConfigurationSKU,
    images,
    SizeMaterialCategory,
    configurationError,
    isReadyToPay,
    isForceToPay,


    ...pdpFilters,

    ...productResult,
  };
};
